import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Re-renders a Custom Liquid block when the selected variant changes.
 *
 * On the product page the variant picker only morphs itself, so blocks that read
 * variant-scoped Liquid (metafields, variant title, …) keep their original markup.
 * This element finds its own counterpart — matched by block id — inside the document
 * the picker already fetched for the new variant, and morphs that content in place.
 *
 * @extends {Component}
 */
class VariantLiquid extends Component {
  /** @type {Element | null} */
  #scope = null;

  connectedCallback() {
    super.connectedCallback();
    this.#scope = this.closest('.shopify-section, dialog');
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    this.#scope = null;
  }

  /**
   * Handles product select event by re-rendering the block's Liquid output.
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const { blockId } = this.dataset;
    if (!blockId) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, newProduct } = detail;

        if (newProduct) {
          this.dataset.productId = newProduct.id;
        } else if (this.dataset.productId && detail.productId && detail.productId !== this.dataset.productId) {
          return;
        }

        const newContent = html.querySelector(`variant-liquid[data-block-id="${CSS.escape(blockId)}"]`);
        if (!newContent) return;

        morph(this, newContent, { childrenOnly: true });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[variant-liquid] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('variant-liquid')) {
  customElements.define('variant-liquid', VariantLiquid);
}
